import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography, styled } from '@mui/material';
import { TFunction } from 'i18next';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useSnackbar } from '@cnx-saas/snackbar';
import { RhfCheckbox, RhfSelect, RhfTextField } from '@cnx-saas/common-fe';
import { UserLanguages } from '@cnx-saas/common';
import { useCompleteRegistrationMutation } from '../../../api/tenant/mutations/complete-registration';

type UserRegistration = {
  firstname: string;
  lastname: string;
  password: string;
  password2: string;
  language: string;
  hasAcceptAgreement: boolean;
};

type RegistrationFormProps = {
  newUserId: string;
  email: string;
};

export const RegistrationForm = ({
  newUserId,
  email,
}: RegistrationFormProps) => {
  const { t } = useTranslation();
  const { addAlert } = useSnackbar();
  const navigate = useNavigate();

  const { control, handleSubmit } = useForm<UserRegistration>({
    resolver: yupResolver(registrationFormSchema(t)),
  });

  const [completeRegistration] = useCompleteRegistrationMutation();

  const onSubmit = async (input: UserRegistration) => {
    const response = await completeRegistration({
      variables: {
        details: {
          firstName: input.firstname,
          lastName: input.lastname,
          password: input.password,
          language: input.language as UserLanguages,
          hasAcceptAgreement: input.hasAcceptAgreement,
          newUserId,
        },
      },
    });
    if (
      response.errors ||
      response.data?.completeRegistration.message !== 'ok'
    ) {
      addAlert({
        type: 'error',
        children: response.data?.completeRegistration.message,
      });
    } else {
      addAlert({
        type: 'success',
        children: t('registration.formLabel.success', {
          defaultValue: 'Your registration was successful',
        }),
      });
      navigate('/login');
    }
  };
  const languageOptions: {
    [key in UserLanguages]: string;
  } = {
    [UserLanguages.DE]: t('userManagement.registration.language.DE', {
      defaultValue: 'German',
    }),
    [UserLanguages.GB]: t('userManagement.registration.language.GB', {
      defaultValue: 'English',
    }),
    [UserLanguages.PL]: t('userManagement.registration.language.PL', {
      defaultValue: 'Polish',
    }),
  };

  function formatLanguage(language: UserLanguages): string {
    const result = languageOptions[language];

    if (!result) throw new Error('Unknown language');

    return result;
  }
  return (
    <Root onSubmit={handleSubmit(onSubmit)}>
      <Box display={'flex'} flexDirection={'column'}>
        <Typography sx={{ mb: 2 }}>{`Email: ${email}`}</Typography>
        <RhfTextField
          control={control}
          name="firstname"
          label={t('registration.formLabel.userFirstName', {
            defaultValue: 'First Name',
          })}
        />
        <RhfTextField
          control={control}
          name="lastname"
          label={t('registration.formLabel.userLastName', {
            defaultValue: 'Last Name',
          })}
        />
        <RhfTextField
          control={control}
          name="password"
          type="password"
          label={t('registration.formLabel.userPassword', {
            defaultValue: 'password',
          })}
        />
        <RhfTextField
          control={control}
          type="password"
          name="password2"
          label={t('registration.formLabel.userPasswordConfirmation', {
            defaultValue: 'Re-enter Password',
          })}
        />
        <RhfSelect
          control={control}
          name="language"
          label={t('registration.formLabel.language', {
            defaultValue: 'Language',
          })}
          values={Object.values(UserLanguages)}
          getValueLabel={formatLanguage}
        />
        <RhfCheckbox
          control={control}
          name="hasAcceptAgreement"
          label={t('registration.formLabel.termsAndCondition', {
            defaultValue:
              'I accept the terms and conditions for Cargonexx GmbH',
          })}
        />

        <Button type="submit">
          {t('registration.formLabel.register', {
            defaultValue: 'Complete Registration',
          })}
        </Button>
      </Box>
    </Root>
  );
};

export const Root = styled('form')({
  width: '100%',
});

export const registrationFormSchema = (t: TFunction) => {
  const fieldRequiredMessage = t(
    'registration.form.validation-error.required',
    {
      defaultValue: 'This field is required.',
    },
  );
  const passwordValidationMessage = {
    minLength: t('registration.form.validation-error.minLength', {
      defaultValue: 'Password must be at least 8 characters long.',
    }),
    hasUpperCase: t('registration.form.validation-error.hasUpperCase', {
      defaultValue: 'Password must contain at least one uppercase letter.',
    }),
    hasLowerCase: t('registration.form.validation-error.hasLowerCase', {
      defaultValue: 'Password must contain at least one lowercase letter.',
    }),
    hasDigit: t('registration.form.validation-error.hasDigit', {
      defaultValue: 'Password must contain at least one digit.',
    }),
    hasSpecialChar: t('registration.form.validation-error.hasSpecialChar', {
      defaultValue: 'Password must contain at least one special character.',
    }),
  };

  return yup.object().shape({
    firstname: yup.string().required(fieldRequiredMessage),
    lastname: yup.string().required(fieldRequiredMessage),
    password: yup
      .string()
      .required(fieldRequiredMessage)
      .test('minLength', passwordValidationMessage.minLength, (value) =>
        value ? value.length >= 8 : false,
      )
      .test('hasUpperCase', passwordValidationMessage.hasUpperCase, (value) =>
        value ? /[A-Z]/.test(value) : false,
      )
      .test('hasLowerCase', passwordValidationMessage.hasLowerCase, (value) =>
        value ? /[a-z]/.test(value) : false,
      )
      .test('hasDigit', passwordValidationMessage.hasDigit, (value) =>
        value ? /\d/.test(value) : false,
      )
      .test(
        'hasSpecialChar',
        passwordValidationMessage.hasSpecialChar,
        (value) => (value ? /[!@#$%^&*(),.?":{}|<>]/.test(value) : false),
      ),
    password2: yup
      .string()
      .oneOf(
        [yup.ref('password')],
        t('registration.form.validation-error.passwordMatch', {
          defaultValue: 'Passwords must match.',
        }),
      )
      .required(fieldRequiredMessage),
    language: yup.string().required(fieldRequiredMessage),
    hasAcceptAgreement: yup
      .boolean()
      .oneOf([true], fieldRequiredMessage)
      .required(fieldRequiredMessage),
  });
};
